import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Colonnes affichables dans le tableau liste facture (hors ACTION, toujours visible). */
export interface FactureListDisplaySettings {
  reference: boolean;
  date: boolean;
  client: boolean;
  codeClient: boolean;
  matricule: boolean;
  nif: boolean;
  email: boolean;
  codeEtablissement: boolean;
  codeEntrepot: boolean;
  ht: boolean;
  tva: boolean;
  ttc: boolean;
  timbreFiscal: boolean;
  netPayer: boolean;
  dateLivraison: boolean;
  note: boolean;
  createdAt: boolean;
  statut: boolean;
}

export const FACTURE_LIST_DISPLAY_DEFAULTS: FactureListDisplaySettings = {
  reference: true,
  date: true,
  client: true,
  codeClient: false,
  matricule: false,
  nif: false,
  email: false,
  codeEtablissement: false,
  codeEntrepot: false,
  ht: false,
  tva: false,
  ttc: true,
  timbreFiscal: false,
  netPayer: false,
  dateLivraison: false,
  note: false,
  createdAt: false,
  statut: true,
};

/** Tous les champs disponibles (alignés sur `documents` + client / établissement enrichis). */
export const FACTURE_LIST_DISPLAY_FIELDS: {
  key: keyof FactureListDisplaySettings;
  label: string;
}[] = [
  { key: 'reference', label: 'Référence' },
  { key: 'date', label: 'Date' },
  { key: 'client', label: 'Client' },
  { key: 'codeClient', label: 'Code client' },
  { key: 'matricule', label: 'Matricule / Identifiant' },
  { key: 'nif', label: 'NIF client' },
  { key: 'email', label: 'Email client' },
  { key: 'codeEtablissement', label: 'Code établissement' },
  { key: 'codeEntrepot', label: 'Code entrepôt' },
  { key: 'ht', label: 'Total HT' },
  { key: 'tva', label: 'Total TVA' },
  { key: 'ttc', label: 'Total TTC' },
  { key: 'timbreFiscal', label: 'Timbre fiscal' },
  { key: 'netPayer', label: 'Net à payer' },
  { key: 'dateLivraison', label: 'Date de livraison' },
  { key: 'note', label: 'Note' },
  { key: 'createdAt', label: 'Date de création' },
  { key: 'statut', label: 'Statut' },
];

/**
 * Préférences d'affichage des colonnes du tableau Facture,
 * sauvegardées par utilisateur connecté (localStorage).
 */
@Injectable({ providedIn: 'root' })
export class FactureListDisplayService {
  private readonly storagePrefix = 'factureListDisplaySettings_';
  private readonly settingsSubject = new BehaviorSubject<FactureListDisplaySettings>({
    ...FACTURE_LIST_DISPLAY_DEFAULTS,
  });

  readonly settings$: Observable<FactureListDisplaySettings> = this.settingsSubject.asObservable();

  get settings(): FactureListDisplaySettings {
    return { ...this.settingsSubject.value };
  }

  /** Charge les prefs pour l'utilisateur connecté (à appeler au mount facture / shared). */
  loadForUser(userId: number | string | null | undefined): FactureListDisplaySettings {
    const key = this.storageKey(userId);
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<FactureListDisplaySettings>;
        const merged: FactureListDisplaySettings = {
          ...FACTURE_LIST_DISPLAY_DEFAULTS,
          ...parsed,
        };
        this.settingsSubject.next(merged);
        return merged;
      }
    } catch {
      /* ignore */
    }
    const defaults = { ...FACTURE_LIST_DISPLAY_DEFAULTS };
    this.settingsSubject.next(defaults);
    return defaults;
  }

  /** Applique sans persister (aperçu live dans le panneau). */
  patch(partial: Partial<FactureListDisplaySettings>): void {
    this.settingsSubject.next({ ...this.settingsSubject.value, ...partial });
  }

  setAll(settings: FactureListDisplaySettings): void {
    this.settingsSubject.next({ ...FACTURE_LIST_DISPLAY_DEFAULTS, ...settings });
  }

  /** Persiste pour la session / utilisateur connecté. */
  saveForUser(userId: number | string | null | undefined, settings?: FactureListDisplaySettings): void {
    const toSave = settings
      ? { ...FACTURE_LIST_DISPLAY_DEFAULTS, ...settings }
      : this.settings;
    this.settingsSubject.next(toSave);
    try {
      localStorage.setItem(this.storageKey(userId), JSON.stringify(toSave));
    } catch {
      /* ignore quota */
    }
  }

  isVisible(column: keyof FactureListDisplaySettings): boolean {
    return this.settingsSubject.value[column] !== false;
  }

  private storageKey(userId: number | string | null | undefined): string {
    const id = userId != null && String(userId).trim() !== '' ? String(userId) : 'anon';
    return `${this.storagePrefix}${id}`;
  }
}
